import { useRef } from "react";
import { motion, useInView } from "framer-motion";

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: delay * 0.001, ease: [0.22, 1, 0.36, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

export default function PrivacyPage() {
  return (
    <section className="pt-32 pb-24 md:pt-44 md:pb-32 px-6 lg:px-12" data-testid="privacy-page">
      <div className="max-w-3xl mx-auto">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#E31212] mb-6">Legal</p>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter mb-10"
            style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
            Privacy Policy
          </h1>
        </Reveal>

        <Reveal delay={100}>
          <div className="prose-invert space-y-8">
            <p className="text-sm text-[#6E7385]">Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>1. Introduction</h2>
              <p className="text-sm text-[#A1A5B3] leading-relaxed">
                Omni ("we," "our," or "us"), a product of Masters Global Inc., is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our 
                AI companion application and related services (the "Service").
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>2. Information We Collect</h2>
              <p className="text-sm text-[#A1A5B3] leading-relaxed mb-3">We collect the following types of information:</p>
              <ul className="space-y-2 text-sm text-[#A1A5B3] leading-relaxed list-disc list-inside">
                <li><strong className="text-white">Account Information:</strong> Name, email address, and password when you create an account.</li>
                <li><strong className="text-white">Conversation Data:</strong> Messages you send to and receive from our AI modules, stored encrypted.</li>
                <li><strong className="text-white">Mood & Wellness Data:</strong> Mood logs and wellness goals you voluntarily provide.</li>
                <li><strong className="text-white">Usage Data:</strong> App interaction patterns, feature usage, and session duration for improving our service.</li>
                <li><strong className="text-white">Payment Information:</strong> Processed securely through Stripe. We never store your card details.</li>
                <li><strong className="text-white">Device Information:</strong> Browser type, operating system, and device identifiers for compatibility.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>3. How We Use Your Information</h2>
              <ul className="space-y-2 text-sm text-[#A1A5B3] leading-relaxed list-disc list-inside">
                <li>To provide, maintain, and improve the Omni AI companion experience.</li>
                <li>To personalize your AI interactions based on your preferences and history.</li>
                <li>To process payments and manage your subscription.</li>
                <li>To send you important service updates and security notifications.</li>
                <li>To comply with legal obligations and enforce our terms of service.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>4. Data Protection & Encryption</h2>
              <p className="text-sm text-[#A1A5B3] leading-relaxed">
                All conversations are encrypted in transit and at rest. We employ industry-standard security measures 
                including TLS 1.3 encryption, secure authentication tokens, and regular security audits. Your AI 
                conversations are never used to train models or shared with third parties.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>5. Data Sharing</h2>
              <p className="text-sm text-[#A1A5B3] leading-relaxed">
                We do <strong className="text-white">not</strong> sell, rent, or trade your personal information. We share data only with:
              </p>
              <ul className="space-y-2 text-sm text-[#A1A5B3] leading-relaxed list-disc list-inside mt-3">
                <li><strong className="text-white">Service Providers:</strong> Stripe (payments), OpenAI (AI processing), Resend (transactional emails) — bound by strict data processing agreements.</li>
                <li><strong className="text-white">Legal Authorities:</strong> Only when required by law, court order, or to protect safety.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>6. Your Rights (GDPR & Global)</h2>
              <p className="text-sm text-[#A1A5B3] leading-relaxed mb-3">You have the right to:</p>
              <ul className="space-y-2 text-sm text-[#A1A5B3] leading-relaxed list-disc list-inside">
                <li><strong className="text-white">Access:</strong> Request a copy of all data we hold about you (available via Settings → Export My Data).</li>
                <li><strong className="text-white">Rectification:</strong> Correct inaccurate personal information.</li>
                <li><strong className="text-white">Erasure:</strong> Request deletion of your account and all associated data.</li>
                <li><strong className="text-white">Portability:</strong> Export your data in JSON or PDF format at any time.</li>
                <li><strong className="text-white">Restriction:</strong> Request that we limit processing of your data.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>6a. Jurisdiction-Specific Notices</h2>
              <ul className="space-y-3 text-sm text-[#A1A5B3] leading-relaxed">
                <li><strong className="text-white">United Kingdom (UK GDPR & DPA 2018):</strong> You may lodge a complaint with the Information Commissioner's Office (ico.org.uk). Special category data (wellbeing, beliefs) is processed only with your explicit consent, which you may withdraw at any time in Settings.</li>
                <li><strong className="text-white">European Union (GDPR):</strong> Legal bases: contract performance (account & subscriptions), explicit consent (Art. 9 wellbeing data, marketing emails), legitimate interest (security & abuse prevention). You may complain to your national supervisory authority.</li>
                <li><strong className="text-white">United States (CCPA/CPRA — California):</strong> We do not sell or share your personal information for cross-context behavioral advertising. You have the right to know, delete, correct, and limit use of sensitive personal information. Exercise these via Settings or privacy@omnimodules.com. We do not discriminate for exercising rights.</li>
                <li><strong className="text-white">Canada (PIPEDA):</strong> We obtain meaningful consent for collection, use and disclosure of personal information. You may withdraw consent, request access, or challenge accuracy by contacting privacy@omnimodules.com, and may complain to the Office of the Privacy Commissioner of Canada.</li>
                <li><strong className="text-white">Nigeria (NDPA 2023):</strong> Data subject rights requests may be sent to privacy@omnimodules.com; complaints may be lodged with the Nigeria Data Protection Commission.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>7. Children's Privacy</h2>
              <p className="text-sm text-[#A1A5B3] leading-relaxed">
                Omni requires users to be at least 13 years old — accounts cannot be created below this age (date of birth
                is verified at signup). Users aged 13–15 require verified parent/guardian consent via an email confirmation
                link before their account is activated. All users under 18 automatically receive teen-safe content settings
                in every jurisdiction, regardless of residence. Family plans provide additional parental controls via the
                Family dashboard.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>8. Data Retention</h2>
              <p className="text-sm text-[#A1A5B3] leading-relaxed">
                We retain your data for as long as your account is active. Free tier accounts with no activity for 12 months 
                may be archived. Upon account deletion, all personal data is permanently removed within 30 days.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>9. Contact Us</h2>
              <p className="text-sm text-[#A1A5B3] leading-relaxed">
                For privacy-related inquiries, contact us at <a href="mailto:privacy@omnimodules.com" className="text-[#E31212] hover:text-[#ff1e1e] transition-colors">privacy@omnimodules.com</a>.<br />
                Masters Global Inc. — Global Operations
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
